// Server state каталога: ключи и хуки TanStack Query (app.md §4–5).
//
// Ключи собраны в одном объекте, а не разбросаны строками по хукам: инвалидация
// после мутации должна попадать ровно в те запросы, которые устарели. Разъехавшиеся
// строковые ключи дают самый неприятный класс багов — список, который «иногда»
// не обновляется после сохранения.
//
// Мутации каталога переживают отсутствие сети (фаза 3, этап B). Путь по умолчанию —
// прямой запрос: онлайн менеджер получает валидацию сервера в форме, как раньше.
// Только `NetworkError` переводит правку в оффлайн-ветку: оптимистичная правка кэша
// (её видно сразу) + запись в `cabinetQueue` (уедет на сервер по возвращении связи).
// Ошибку сервера (`HttpError`) оффлайн-ветка НЕ перехватывает — форма покажет её
// по полям, как и в онлайне.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { uuid } from '@/shared/lib';
import {
  enqueueCabinetOp,
  invalidateUnlessPending,
  onlineOrQueued,
  type CabinetAction,
  type CabinetEntity,
  type Pending,
} from '@/shared/offline';

import {
  catalogApi,
  type CatalogListFilters,
  type LocationProductFilters,
  type MasterProductFilters,
} from './api';
import {
  applyOptimistic,
  draftCategory,
  draftLocationProduct,
  draftMasterProduct,
  findCategory,
  findRow,
  markAddedToMenu,
} from './optimistic';
import type {
  AddFromCatalogResponse,
  Category,
  DeleteMasterProductResponse,
  LocationProduct,
  MasterProduct,
} from './types';

export const catalogKeys = {
  all: ['catalog'] as const,
  categories: (filters?: CatalogListFilters) => ['catalog', 'categories', filters ?? {}] as const,
  bands: () => ['catalog', 'bands'] as const,
  productTypes: () => ['catalog', 'product-types'] as const,
  // Разметка для точки (`location`) живёт в фильтрах, а не в отдельном ключе: префикс
  // `['catalog','master-products']` общий у всех списков каталога, и по нему офлайн-ветка
  // ищет строку эталона (`findRow`), а мутации — что инвалидировать.
  masterProducts: (filters?: MasterProductFilters) =>
    ['catalog', 'master-products', filters ?? {}] as const,
  locationProducts: (filters: LocationProductFilters) =>
    ['catalog', 'location-products', filters] as const,
  locations: () => ['catalog', 'locations'] as const,
} as const;

// --- Оффлайн-ветка мутаций --------------------------------------------------
//
// `onlineOrQueued`/`invalidateUnlessPending`/`Pending` живут в `@/shared/offline`
// (mutationHelpers.ts) — общие для всех доменов кабинета, не только каталога.

type EnqueueArgs = {
  clientId: string;
  entity: CabinetEntity;
  action: CabinetAction;
  targetId: string | null;
  payload: Record<string, unknown>;
  locationId?: string | null;
  baseUpdatedAt?: string | null;
};

function queueOp(args: EnqueueArgs): Promise<void> {
  return enqueueCabinetOp({
    clientId: args.clientId,
    entity: args.entity,
    action: args.action,
    targetId: args.targetId,
    payload: args.payload,
    locationId: args.locationId ?? null,
    baseUpdatedAt: args.baseUpdatedAt ?? null,
  });
}

// --- Справочники ------------------------------------------------------------

export function useCategories(filters: CatalogListFilters = {}) {
  return useQuery({
    queryKey: catalogKeys.categories(filters),
    queryFn: () => catalogApi.listCategories(filters),
  });
}

export function useShelfLifeBands() {
  return useQuery({
    queryKey: catalogKeys.bands(),
    queryFn: () => catalogApi.listBands(),
    // Границы полок задаёт платформа и меняет крайне редко — держим дольше обычного,
    // чтобы фильтр не дёргал сервер при каждом заходе на экран.
    staleTime: 60 * 60_000,
  });
}

export function useProductTypes() {
  return useQuery({
    queryKey: catalogKeys.productTypes(),
    queryFn: () => catalogApi.listProductTypes(),
    staleTime: 60 * 60_000,
  });
}

export function useLocations() {
  return useQuery({
    queryKey: catalogKeys.locations(),
    queryFn: () => catalogApi.listLocations(),
  });
}

// --- Мутации справочника категорий ------------------------------------------

export function useSaveCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, body }: { id?: string; body: Partial<Category> }) =>
      onlineOrQueued<Category & Pending>(
        () => (id ? catalogApi.updateCategory(id, body) : catalogApi.createCategory(body)),
        async () => {
          const clientId = id ?? uuid();
          const payload = body as Record<string, unknown>;
          if (id) {
            applyOptimistic(queryClient, {
              entity: 'category',
              action: 'update',
              id,
              patch: payload,
            });
          } else {
            applyOptimistic(queryClient, {
              entity: 'category',
              action: 'create',
              row: draftCategory(clientId, body),
            });
          }
          await queueOp({
            clientId,
            entity: 'category',
            action: id ? 'update' : 'create',
            targetId: id ?? null,
            payload,
            baseUpdatedAt: id
              ? ((findRow(queryClient, 'category', id)?.updated_at as string | undefined) ?? null)
              : null,
          });
          return draftCategory(clientId, body) as Category & Pending;
        },
      ),
    onSuccess: (result) => {
      // Инвалидируется весь каталог, а не только список категорий: правка срока
      // меняет чипы категорий в обоих списках продуктов, и точечная инвалидация
      // оставила бы там старую длительность.
      invalidateUnlessPending(queryClient, result, catalogKeys.all);
    },
  });
}

export function useDeleteCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      onlineOrQueued<Pending | void>(
        () => catalogApi.deleteCategory(id),
        async () => {
          applyOptimistic(queryClient, { entity: 'category', action: 'delete', id });
          await queueOp({
            clientId: uuid(),
            entity: 'category',
            action: 'delete',
            targetId: id,
            payload: {},
          });
          return { _pending: true };
        },
      ),
    onSuccess: (result) => {
      invalidateUnlessPending(queryClient, result as Pending, catalogKeys.all);
    },
  });
}

// --- Родительский каталог ----------------------------------------------------

export function useMasterProducts(filters: MasterProductFilters = {}) {
  return useQuery({
    queryKey: catalogKeys.masterProducts(filters),
    queryFn: () => catalogApi.listMasterProducts(filters),
  });
}

export function useSaveMasterProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, body }: { id?: string; body: Partial<MasterProduct> }) =>
      onlineOrQueued<MasterProduct & Pending>(
        () =>
          id ? catalogApi.updateMasterProduct(id, body) : catalogApi.createMasterProduct(body),
        async () => {
          const clientId = id ?? uuid();
          const payload = body as Record<string, unknown>;
          const categoryDetail = body.default_category
            ? findCategory(queryClient, body.default_category)
            : undefined;
          if (id) {
            const patch: Record<string, unknown> = { ...payload };
            if (categoryDetail) patch.default_category_detail = categoryDetail;
            applyOptimistic(queryClient, {
              entity: 'masterProduct',
              action: 'update',
              id,
              patch,
            });
          } else {
            applyOptimistic(queryClient, {
              entity: 'masterProduct',
              action: 'create',
              row: draftMasterProduct(clientId, payload, categoryDetail),
            });
          }
          await queueOp({
            clientId,
            entity: 'masterProduct',
            action: id ? 'update' : 'create',
            targetId: id ?? null,
            payload,
            baseUpdatedAt: id
              ? ((findRow(queryClient, 'masterProduct', id)?.updated_at as string | undefined) ??
                null)
              : null,
          });
          return draftMasterProduct(clientId, payload, categoryDetail) as MasterProduct & Pending;
        },
      ),
    onSuccess: (result) => {
      // Меню точек НЕ инвалидируется: правка эталона по инварианту каталога не
      // применяется к существующим копиям, и обновлять их здесь было бы неправдой.
      if ((result as Pending)?._pending) return;
      void queryClient.invalidateQueries({ queryKey: ['catalog', 'master-products'] });
      void queryClient.invalidateQueries({ queryKey: ['catalog', 'categories'] });
    },
  });
}

export function useDeleteMasterProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      onlineOrQueued<DeleteMasterProductResponse & Pending>(
        () => catalogApi.deleteMasterProduct(id),
        async () => {
          applyOptimistic(queryClient, { entity: 'masterProduct', action: 'delete', id });
          await queueOp({
            clientId: uuid(),
            entity: 'masterProduct',
            action: 'delete',
            targetId: id,
            payload: {},
          });
          // Число отвязанных копий знает только сервер — до синка показываем 0.
          return { detached_copies: 0, _pending: true };
        },
      ),
    onSuccess: (result) => {
      // А вот удаление копии затрагивает: у них обнуляется master_product,
      // и признак «из родительского каталога» в меню точки меняется.
      invalidateUnlessPending(queryClient, result, catalogKeys.all);
    },
  });
}

// --- Меню точки --------------------------------------------------------------

export function useLocationProducts(filters: LocationProductFilters) {
  return useQuery({
    queryKey: catalogKeys.locationProducts(filters),
    queryFn: () => catalogApi.listLocationProducts(filters),
    enabled: Boolean(filters.location),
  });
}

export function useSaveLocationProduct(location: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, body }: { id?: string; body: Partial<LocationProduct> }) =>
      onlineOrQueued<LocationProduct & Pending>(
        () =>
          id
            ? catalogApi.updateLocationProduct(id, body)
            : catalogApi.createLocationProduct(location, body),
        async () => {
          const clientId = id ?? uuid();
          const payload = body as Record<string, unknown>;
          const categoryDetail =
            typeof body.category === 'string'
              ? findCategory(queryClient, body.category)
              : undefined;
          if (id) {
            const patch: Record<string, unknown> = { ...payload, is_locally_modified: true };
            if (categoryDetail) patch.category_detail = categoryDetail;
            applyOptimistic(queryClient, {
              entity: 'locationProduct',
              action: 'update',
              id,
              patch,
            });
          } else {
            applyOptimistic(queryClient, {
              entity: 'locationProduct',
              action: 'create',
              row: draftLocationProduct(clientId, location, payload, categoryDetail),
            });
          }
          await queueOp({
            clientId,
            entity: 'locationProduct',
            action: id ? 'update' : 'create',
            targetId: id ?? null,
            payload,
            locationId: location,
            baseUpdatedAt: id
              ? ((findRow(queryClient, 'locationProduct', id)?.updated_at as string | undefined) ??
                null)
              : null,
          });
          return draftLocationProduct(
            clientId,
            location,
            payload,
            categoryDetail,
          ) as LocationProduct & Pending;
        },
      ),
    onSuccess: (result) => {
      invalidateUnlessPending(queryClient, result, ['catalog', 'location-products']);
    },
  });
}

export function useDeleteLocationProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      onlineOrQueued<Pending | void>(
        () => catalogApi.deleteLocationProduct(id),
        async () => {
          applyOptimistic(queryClient, { entity: 'locationProduct', action: 'delete', id });
          await queueOp({
            clientId: uuid(),
            entity: 'locationProduct',
            action: 'delete',
            targetId: id,
            payload: {},
          });
          return { _pending: true };
        },
      ),
    onSuccess: (result) => {
      invalidateUnlessPending(queryClient, result as Pending, ['catalog', 'location-products']);
    },
  });
}

/** Префикс ключа меню экрана печати — см. инвалидацию в `useAddFromCatalog`. */
const PRINTING_MENU_KEY = ['printing', 'menu'] as const;

export function useAddFromCatalog(location: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (masterProductIds: readonly string[]) =>
      onlineOrQueued<AddFromCatalogResponse & Pending>(
        () => catalogApi.addFromCatalog(location, masterProductIds),
        async () => {
          // Оптимистично: по каждому эталону из кэша строим копию меню точки.
          // Эталона нет в кэше — строка появится после синка, не раньше.
          const created: LocationProduct[] = [];
          for (const masterId of masterProductIds) {
            const master = findRow(queryClient, 'masterProduct', masterId) as
              MasterProduct | undefined;
            if (!master) continue;
            // Категория — из справочника, если он в кэше (там свежее: его могли править
            // офлайн), иначе из самой строки эталона: `default_category_detail` сервер
            // кладёт в каждую строку каталога. Справочник экран меню не загружает, и без
            // второго источника строка уходила в таблицу без `category_detail` —
            // таблица падала на чтении цвета чипа (B-047).
            const categoryDetail =
              (typeof master.default_category === 'string'
                ? findCategory(queryClient, master.default_category)
                : undefined) ?? master.default_category_detail;
            // Данных на чип срока нет и там (синтетический эталон, заведённый офлайн
            // без справочника) — строка появится после синка, как и без эталона в кэше.
            // Строка без `category_detail` в списке меню — это упавший экран, а не пустой чип.
            if (!categoryDetail) continue;
            const row = draftLocationProduct(
              uuid(),
              location,
              {
                name: master.name,
                category: master.default_category,
                product_type: master.product_type ?? null,
                storage_conditions: master.storage_conditions ?? '',
                sku: master.sku ?? '',
              },
              categoryDetail,
            );
            row.master_product = masterId;
            row.is_local = false;
            applyOptimistic(queryClient, {
              entity: 'locationProduct',
              action: 'create',
              row,
            });
            created.push(row as unknown as LocationProduct);
          }
          // Разметка каталога этой точки — тоже сразу (T-045): строка уходит из блока
          // «Новое в каталоге сети», модал показывает её «уже в меню».
          markAddedToMenu(queryClient, location, masterProductIds);
          await queueOp({
            clientId: uuid(),
            entity: 'locationProduct',
            action: 'addFromCatalog',
            targetId: null,
            payload: { masterProductIds: [...masterProductIds] },
            locationId: location,
          });
          return { created, skipped_master_ids: [], unavailable_master_ids: [], _pending: true };
        },
      ),
    onSuccess: (result) => {
      if (result._pending) return;
      void queryClient.invalidateQueries({ queryKey: ['catalog', 'location-products'] });
      void queryClient.invalidateQueries({ queryKey: ['catalog', 'master-products'] });
      // Меню экрана печати (`printingKeys.menu` в `modules/printing`) живёт 10 минут:
      // без этого добавленная позиция не появилась бы на печати того же устройства.
      // Литерал, а не импорт: `printing` сам импортирует типы `catalog`, и обратный
      // импорт утянул бы экран печати в чанк каталога (app.md §3). Что префикс
      // не разъехался с настоящим ключом, сторожит `CatalogNewsBlock.test.tsx`.
      void queryClient.invalidateQueries({ queryKey: PRINTING_MENU_KEY });
    },
  });
}

/** Сколько новинок блок берёт за раз — потолок страницы сервера (`max_page_size`). */
const NEWS_PAGE_SIZE = 200;

/**
 * Новые для точки позиции каталога сети — блок «Новое в каталоге сети» (T-045).
 *
 * Тот же список эталонов, что у модала «Добавить из каталога», — строки полной формы:
 * по ним офлайн-«Добавить» строит копию меню (`findRow`), и облегчённая строка без
 * категории уронила бы экран (B-047). Считает сервер: «что я уже видел на этом
 * устройстве» разошлось бы между телефоном и планшетом и между двумя менеджерами.
 */
export function useCatalogNews(location: string, enabled: boolean) {
  const filters: MasterProductFilters = {
    location,
    new_for_location: true,
    page_size: NEWS_PAGE_SIZE,
  };
  return useQuery({
    queryKey: catalogKeys.masterProducts(filters),
    queryFn: () => catalogApi.listMasterProducts(filters),
    enabled: enabled && Boolean(location),
    // Владелец просил подсветку «при заходе» в меню точки: общий `staleTime` (60 с)
    // показал бы список, снятый минуту назад на другом экране.
    refetchOnMount: 'always',
    // Только строки с явным `is_new`. Сервер без T-045 (откат) параметр не знает
    // и отдаёт весь каталог без разметки — блок тогда пуст, а не «весь каталог новый».
    select: (page) => page.results.filter((row) => row.is_new === true),
  });
}
